import React, { useId } from "react";
import classNames from "classnames";
import { Icons } from "../../../atoms/icon";
import {
  MarkdownEditor,
  MenuButtonOptions,
  TitleWrap,
  SupportTagNames,
} from "@graffer-inc/console-design-kit";
import { ApplyMarkdown } from "../../graffer-utility-tools/markdown-preview/markdown-preview";
import { useMarkdownEditorController } from "./use-markdown-editor-controller";
import { defaultAllowedTagNames } from "../../../../utils/markdown/processor";

type CloudGovMarkdownEditorProps = {
  value?: string;
  previewHeaderText?: string;
  allowedTagNames?: SupportTagNames[]; // 許可するタグを指定(それによってエディタのボタンが表示される)
  menuButtonOptions?: MenuButtonOptions; // ボタンのラベルやアイコンを差し替えたい時に使用
  titleProps: Omit<React.ComponentProps<typeof TitleWrap>, "id">;
  maxLength?: number;
  onChange: (value: string) => void;
  errorMessages?: string | string[] | undefined;
};

const DefaultMenuButtonOptions: MenuButtonOptions = {
  h2: {
    label: "見出し1",
    icon: <Icons.Heading1Icon />,
  },
  h3: {
    label: "見出し2",
    icon: <Icons.Heading2Icon />,
  },
};

export const CloudGovMarkdownEditor: React.FC<CloudGovMarkdownEditorProps> = ({
  value = "",
  titleProps,
  menuButtonOptions = DefaultMenuButtonOptions,
  allowedTagNames = defaultAllowedTagNames,
  previewHeaderText,
  maxLength = 500,
  onChange,
  errorMessages,
}) => {
  const id = `markdown-editor-${useId()}`;

  const { controller } = useMarkdownEditorController({
    onChange,
    errorMessages,
  });

  return (
    <>
      {titleProps && (
        <TitleWrap
          title={titleProps?.title}
          id={id}
          description={titleProps?.description}
        />
      )}

      <MarkdownEditor
        value={value}
        onChange={onChange}
        isError={controller.formState.type === "error"}
        allowedTagNames={allowedTagNames}
        editMenuBarOptions={{
          menuButton: menuButtonOptions,
        }}
        editAreaOptions={{
          attributes: {
            id,
            rows: 15,
            onBlur: controller.onBlur,
            "aria-errormessage": `${id}-error`,
            "aria-describedby": classNames(
              `form-description-${id}`,
              controller.formState.type === "error" && `${id}-error`,
            ),
            "aria-invalid": controller.formState.type === "error",
          },
        }}
        previewAreaOptions={{
          content: <ApplyMarkdown markdown={value ?? ""} />,
        }}
        previewMenuBarOptions={{
          description: previewHeaderText,
        }}
        charCounterOptions={{
          show: true,
          maxLength: maxLength,
        }}
      />

      {controller.formState.type === "error" && (
        <div id={`${id}-error`} className="markdown-editor-error-container">
          {controller.formState.errorMessage}
        </div>
      )}
    </>
  );
};
